import { useEffect, useRef, useState } from 'react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MapPin, Plus, Sparkles, Upload, Link2, X, RefreshCw } from 'lucide-react';
import { EntityPicker } from './EntityPicker';

const SLOTS = [
  { key: 'location_id', label: 'Opening Location', color: '#10B981', hint: 'Used for the opening frame (and the closing frame unless a closing location is set)' },
  { key: 'closing_location_id', label: 'Closing Location', color: '#00F0FF', hint: 'Only used for the closing frame' },
];

function LocationSlot({ slot, location, projectId, scene, imageProvider, onAssign, onRemove, onLocationChanged, uploadRef }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: scene?.setting || '' });
  const [busy, setBusy] = useState(null);
  const [picker, setPicker] = useState(false);
  const [desc, setDesc] = useState(location?.description || '');
  useEffect(() => { setDesc(location?.description || ''); }, [location?.id, location?.description]);

  const genImage = async (loc, description) => {
    setBusy('gen');
    try {
      const res = await api.post(`/projects/${projectId}/locations/${loc.id}/generate-image`, { description, image_provider: imageProvider }, { timeout: 180000 });
      onLocationChanged(res.data); toast.success('Location image generated');
    } catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === 'string' ? (d.startsWith('MISSING_KEY:') ? d.split('|')[1] : d) : 'Image generation failed'); }
    finally { setBusy(null); }
  };
  const create = async (withImage) => {
    if (!form.name.trim()) { toast.error('Give the location a name'); return; }
    setBusy('create');
    try {
      const res = await api.post(`/projects/${projectId}/locations`, { name: form.name.trim(), description: form.description.trim() });
      await onAssign(res.data);
      setCreating(false);
      if (withImage && form.description.trim()) await genImage(res.data, form.description.trim());
    } catch { toast.error('Could not create location'); }
    finally { setBusy(null); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file || !location) return;
    setBusy('upload');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/projects/${projectId}/locations/${location.id}/upload-image`, fd);
      onLocationChanged(res.data); toast.success('Location image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setBusy(null); if (uploadRef.current) uploadRef.current.value = ''; }
  };
  const saveDesc = async () => {
    if (!location || desc.trim() === (location.description || '')) return;
    try { const res = await api.put(`/projects/${projectId}/locations/${location.id}`, { description: desc.trim() }); onLocationChanged(res.data); }
    catch { toast.error('Could not save description'); }
  };

  return (
    <div className="ff-card p-5" data-testid={`location-slot-${slot.key}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold flex items-center gap-1.5" style={{ color: slot.color }}><MapPin className="w-3.5 h-3.5" /> {slot.label}</p>
        <div className="flex items-center gap-2">
          <button data-testid={`location-pick-${slot.key}`} onClick={() => setPicker(true)} className="text-[10px] flex items-center gap-1" style={{ color: slot.color }}><Link2 className="w-3 h-3" /> {location ? 'Change' : 'Use existing location'}</button>
          {location && slot.key === 'closing_location_id' && (
            <button data-testid="location-remove-closing" onClick={onRemove} className="text-[10px] text-[#8A8A93] hover:text-red-400 flex items-center gap-1"><X className="w-3 h-3" /> Remove</button>
          )}
        </div>
      </div>
      {location ? (
        <>
          <div className="mb-3 rounded-lg overflow-hidden border border-white/[0.06] bg-[#0a0a0e] aspect-video flex items-center justify-center">
            {location.image_path ? <img src={getImageUrl(location.image_path)} alt={location.name} className="w-full h-full object-cover" /> : <p className="text-xs text-[#55555C]">No reference image yet — generate or upload one</p>}
          </div>
          <p data-testid={`location-name-${slot.key}`} className="text-sm font-semibold text-white mb-1.5">{location.name}</p>
          <div className="flex gap-2 items-end">
            <textarea data-testid={`location-desc-${slot.key}`} value={desc} onChange={e => setDesc(e.target.value)} onBlur={saveDesc} className="ff-input flex-1 h-16 resize-none text-xs" placeholder="Location description…" />
            <div className="flex flex-col gap-1">
              <button data-testid={`location-gen-${slot.key}`} onClick={() => genImage(location, desc.trim())} disabled={!!busy || !desc.trim()}
                className="text-xs px-3 py-2 rounded-lg bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap">
                {busy === 'gen' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : location.image_path ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                {location.image_path ? 'Regen' : 'Generate'}
              </button>
              <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={upload} />
              <button data-testid={`location-upload-${slot.key}`} onClick={() => uploadRef.current?.click()} disabled={!!busy}
                className="text-xs px-3 py-2 rounded-lg bg-white/[0.04] text-[#8A8A93] hover:text-white border border-white/[0.06] disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap">
                {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
              </button>
            </div>
          </div>
        </>
      ) : creating ? (
        <div className="space-y-2" data-testid={`location-create-form-${slot.key}`}>
          <input data-testid={`location-new-name-${slot.key}`} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Location name (e.g. Harbour warehouse — night)" className="ff-input w-full text-xs" />
          <textarea data-testid={`location-new-desc-${slot.key}`} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the place: architecture, light, weather, era…" className="ff-input w-full h-16 resize-none text-xs" />
          <div className="flex gap-2">
            <button data-testid={`location-create-gen-${slot.key}`} onClick={() => create(true)} disabled={!!busy} className="text-xs px-3 py-2 rounded-lg bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 hover:bg-[#10B981]/25 disabled:opacity-40 flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Create & generate image
            </button>
            <button data-testid={`location-create-only-${slot.key}`} onClick={() => create(false)} disabled={!!busy} className="text-xs px-3 py-2 rounded-lg bg-white/[0.04] text-[#8A8A93] border border-white/[0.06] hover:text-white disabled:opacity-40">Create (add image later)</button>
            <button onClick={() => setCreating(false)} className="text-xs text-[#8A8A93] hover:text-white ml-auto">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-white/10 p-5 text-center">
          <p className="text-xs text-[#8A8A93] mb-3">{slot.hint}</p>
          {slot.key === 'location_id' && scene?.suggested_location && (
            <button data-testid="location-use-suggested" disabled={busy === 'suggest'} onClick={async () => {
              setBusy('suggest');
              try {
                const s = scene.suggested_location;
                const loc = s.project_id === projectId ? (await api.get(`/projects/${projectId}/locations`)).data.find(l => l.id === s.id) : (await api.post(`/projects/${projectId}/locations/import`, { source_id: s.id })).data;
                if (loc) await onAssign(loc); else toast.error('Suggested location no longer exists');
              } catch { toast.error('Could not use suggested location'); }
              finally { setBusy(null); }
            }} className="mb-3 mx-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs hover:bg-[#00F0FF]/20 disabled:opacity-50">
              {busy === 'suggest' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              AI suggests: <span className="font-semibold">{scene.suggested_location.name}</span><span className="text-[#8A8A93]">· {scene.suggested_location.project_name}</span> — use it
            </button>
          )}
          <div className="flex justify-center gap-2">
            <button data-testid={`location-new-${slot.key}`} onClick={() => setCreating(true)} className="text-xs px-3 py-2 rounded-lg bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 hover:bg-[#10B981]/25 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New location</button>
            <button onClick={() => setPicker(true)} className="text-xs px-3 py-2 rounded-lg bg-white/[0.04] text-[#8A8A93] border border-white/[0.06] hover:text-white flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Use existing location</button>
          </div>
        </div>
      )}
      {picker && <EntityPicker kind="location" projectId={projectId} excludeIds={location ? [location.id] : []} onClose={() => setPicker(false)} onPick={onAssign} title={`${slot.label}: choose a location`} />}
    </div>
  );
}

// Opening + optional closing location for a scene. Assigning syncs the legacy location_ref_path on the backend.
export function SceneLocations({ projectId, scene, imageProvider, onSceneUpdated }) {
  const [locations, setLocations] = useState([]);
  const [showClosing, setShowClosing] = useState(!!scene?.closing_location_id);
  const openRef = useRef(null), closeRef = useRef(null);
  useEffect(() => { setShowClosing(!!scene?.closing_location_id); }, [scene?.id, scene?.closing_location_id]);
  useEffect(() => {
    api.get(`/projects/${projectId}/locations`).then(r => setLocations(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [projectId, scene?.id]);

  const byId = (id) => locations.find(l => l.id === id) || null;
  const assign = (slotKey) => async (loc) => {
    setLocations(prev => prev.some(l => l.id === loc.id) ? prev.map(l => l.id === loc.id ? { ...l, ...loc } : l) : [...prev, loc]);
    const res = await api.put(`/projects/${projectId}/scenes/${scene.id}`, { [slotKey]: loc.id });
    onSceneUpdated(res.data);
    toast.success(`${slotKey === 'location_id' ? 'Opening' : 'Closing'} location set: ${loc.name}`);
  };
  const removeClosing = async () => {
    const res = await api.put(`/projects/${projectId}/scenes/${scene.id}`, { closing_location_id: '' });
    onSceneUpdated(res.data); setShowClosing(false);
  };
  const changed = (loc) => setLocations(prev => prev.map(l => l.id === loc.id ? { ...l, ...loc } : l));

  return (
    <div className="space-y-4" data-testid="scene-locations">
      <LocationSlot slot={SLOTS[0]} location={byId(scene?.location_id)} projectId={projectId} scene={scene} imageProvider={imageProvider}
        onAssign={assign('location_id')} onLocationChanged={changed} uploadRef={openRef} />
      {showClosing ? (
        <LocationSlot slot={SLOTS[1]} location={byId(scene?.closing_location_id)} projectId={projectId} scene={scene} imageProvider={imageProvider}
          onAssign={assign('closing_location_id')} onRemove={removeClosing} onLocationChanged={changed} uploadRef={closeRef} />
      ) : (
        <button data-testid="add-closing-location" onClick={() => setShowClosing(true)} className="text-xs text-[#00F0FF] hover:text-[#33F3FF] flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add closing location <span className="text-[#55555C]">— if the scene ends somewhere else</span>
        </button>
      )}
    </div>
  );
}
